import { useQuery } from "@tanstack/react-query";
import { BriefSubscribe } from "@/components/BriefSubscribe";
import { InviteColleague } from "@/components/InviteColleague";
import { PublicShell } from "@/components/layout/PublicShell";
import { useCity } from "@/contexts/CityContext";
import { getBriefIssues, getLatestBrief } from "@/lib/brief";
import { SEO, briefArchiveSeoMetadata } from "@/lib/seo";
import { buildBriefArchiveStructuredData } from "@/lib/structured-data";
import { AlertCircle, Mail } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Button, EmptyState, Panel, SectionHead } from "@/components/system";

export default function BriefArchive() {
  const { city } = useCity();
  const latest = useQuery({
    queryKey: ["brief-latest", city.slug],
    queryFn: () => getLatestBrief(city.slug),
    retry: false,
  });
  const issues = useQuery({
    queryKey: ["brief-issues", city.slug],
    queryFn: () => getBriefIssues(city.slug),
    retry: false,
  });
  const metadata = briefArchiveSeoMetadata();

  return (
    <PublicShell>
      {/* Thin until the first issue is sent: noindex,follow, flipping to
          indexable automatically once an issue exists (SEO audit report 03).
          Waits for the issue list so it never overrides the prerendered head. */}
      {issues.isSuccess ? (
        <SEO
          title={metadata.title}
          description={metadata.description}
          path={metadata.path}
          noindex={issues.data.length === 0}
          follow
          jsonLd={buildBriefArchiveStructuredData()}
        />
      ) : null}
      <div className="ds-container">
        <header className="ds-section" style={{ paddingBottom: 32 }}>
          <SectionHead
            level={1}
            eyebrow="Weekly · Tuesday morning"
            title={`${city.name} Referral Brief`}
            lead="A plain clinical summary of referral availability changes, newly listed specialists and current public-health alerts."
          />
        </header>

        <div className="ds-split">
          <div className="ds-stack" style={{ gap: 16 }}>
            {latest.isLoading ? (
              <div className="ds-skeleton" style={{ height: 220 }} aria-label="Loading the latest brief" />
            ) : latest.data ? (
              <Panel as="article" className="pb-brief-issue">
                <p className="ds-eyebrow">{formatIssueDate(latest.data.issueDate)}</p>
                <h2 className="ds-display-2">{latest.data.subject}</h2>
                <div className="pb-brief-issue__html ds-prose" dangerouslySetInnerHTML={{ __html: latest.data.html }} />
              </Panel>
            ) : isNotFound(latest.error) || (issues.isSuccess && issues.data.length === 0) ? (
              // Nothing has been sent yet. That is a normal state, not a failure.
              <EmptyState icon={<Mail />} title="The first issue goes out on a Tuesday">
                Past issues will be listed here once it has been sent. Subscribe below to get it in your inbox.
              </EmptyState>
            ) : (
              <EmptyState
                tone="error"
                icon={<AlertCircle />}
                title="The latest brief didn't load"
                action={<Button variant="secondary" onClick={() => void latest.refetch()}>Try again</Button>}
              >
                Check your connection and try again.
              </EmptyState>
            )}
          </div>

          <div className="ds-stack" style={{ gap: 16 }}>
            <Panel tight aria-labelledby="issue-list-title">
              <h2 id="issue-list-title" className="ds-title">Past issues</h2>
              {issues.isLoading ? <p className="ds-small">Loading the archive…</p> : null}
              {issues.isError ? (
                <div className="ds-stack" style={{ gap: 10 }}>
                  <p className="ds-small">The issue list didn't load.</p>
                  <div><Button size="sm" variant="secondary" onClick={() => void issues.refetch()}>Try again</Button></div>
                </div>
              ) : null}
              {issues.data?.length === 0 ? <p className="ds-small">No issues have been sent yet.</p> : null}
              {issues.data && issues.data.length > 0 ? (
                <ol className="ds-stack" style={{ gap: 10, listStyle: "none", margin: 0, padding: 0 }}>
                  {issues.data.map((issue) => (
                    <li key={issue.issueDate} className="ds-stack" style={{ gap: 2 }}>
                      <time className="ds-eyebrow" dateTime={issue.issueDate}>{formatIssueDate(issue.issueDate)}</time>
                      <span className="ds-small">{issue.subject}</span>
                    </li>
                  ))}
                </ol>
              ) : null}
            </Panel>
            <InviteColleague />
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <BriefSubscribe sourcePath="/brief" />
        </div>
      </div>
    </PublicShell>
  );
}

function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

function formatIssueDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("en-AU", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(date);
}
